import { match } from 'ts-pattern'
import { ApiError } from '../../../utils/api-client/api-error.ts'
import {
  IntegrationEndpointFixedProblemSchema,
  IntegrationValidationFailureResponseSchema,
} from '../../../utils/api-client/api-schemas.ts'
import { getEndpointFixedMessage, getSecretGroup, type IntegrationField } from './integration-instance-utils.ts'

/**
 * Where a failed save is shown. `anchoredFieldKeys` are marked invalid; only the first of them
 * carries `fieldMessage`, so a five-field form does not repeat one sentence five times. A failure
 * naming no field the user can act on sets `formMessage` instead and anchors nothing.
 */
export type IntegrationSaveError = {
  anchoredFieldKeys: string[]
  fieldMessage?: string
  formMessage?: string
}

/** The Name field's key. It is rendered by the drawers rather than by `TOOL_FIELD_CONFIG`. */
export const NAME_FIELD_KEY = 'name'

const DUPLICATE_NAME_MESSAGE = 'Name already exists'

/**
 * Which write hit the conflict. It decides less than it used to: both operations answer the same
 * duplicate shapes — the spec documents `POST /api/v1/integrations` and `PUT /api/v1/integrations/{id}`
 * as each answering a `oneOf` of `DuplicateIntegrationNameProblem` and `DuplicateGitHubAppProblem`,
 * and `PUT` as adding `IntegrationModifiedConcurrentlyProblem`. The runtime also answers the
 * endpoint-fixed refusal on `PUT`, which the spec does not publish yet — see the "Ahead of the spec"
 * entry in `unmapped-schemas.md`. Either way a `409` is told apart by the problem's `type` rather
 * than by the request that hit it.
 *
 * The operation now decides only what to do with a `409` carrying no `type` at all — a body older
 * than the discriminator. A create reads one as a name already in use: that is the conflict a create
 * was understood to have, and what the design spec anchors to the Name field. An update reads
 * nothing into it.
 *
 * A `409` that does name a type, just not one this build knows, is never guessed at by either
 * operation. That case is precisely where asserting a cause produces the wrong one — the bug this
 * dispatch exists to fix — so it is left to the error toast and the platform's own words.
 */
export type IntegrationWriteOperation = 'create' | 'update'

const CONCURRENT_MODIFICATION_MESSAGE =
  'Someone else saved this integration while you were editing it, so nothing was written. Close and reopen the drawer to see the stored values before applying your change again.'

/**
 * The two problems this drawer reports itself that name no validation reason. Matched on the
 * problem's RFC 9457 `type` rather than on its status, because each shares a status with a shape
 * that means something else: a 422 is otherwise `IntegrationValidationFailedProblem`, and a 500 is
 * otherwise the generic `AboutBlankProblem`, which the error toast already covers.
 */
const ENDPOINT_MOVED_PROBLEM_TYPE = 'pixee:problems/api/integrations/endpoint-moved-without-credentials'

const CREDENTIALS_UNREADABLE_PROBLEM_TYPE = 'pixee:problems/api/integrations/credentials-unreadable'

/**
 * The four conflicts a write can answer, each naming a different cause under the one `409` status.
 * Matched on the problem's `type` because the status alone cannot tell them apart, and a conflict
 * reported as the wrong one sends the user to fix something that is not broken.
 */
const ENDPOINT_FIXED_PROBLEM_TYPE = 'pixee:problems/api/integrations/integration-endpoint-fixed'

const CONCURRENT_MODIFICATION_PROBLEM_TYPE = 'pixee:problems/api/integrations/integration-modified-concurrently'

const DUPLICATE_NAME_PROBLEM_TYPE = 'pixee:problems/api/integrations/duplicate-integration-name'

const DUPLICATE_GITHUB_APP_PROBLEM_TYPE = 'pixee:problems/api/integrations/duplicate-github-app'

/** GitHub's App id field, which is what a duplicate-App refusal is about. */
const APP_ID_FIELD_KEY = 'app_id'

/**
 * Connecting one App twice would enroll every repository on it through both connections, which is
 * why the platform refuses rather than leaving the duplication to be discovered later.
 *
 * Used only when the problem's own `detail` is blank. The platform's detail names the App id and the
 * host it is already connected on — "App 4980208 on https://api.github.com …" — which this cannot,
 * and which is what tells the user whether the clash is the App they meant (ISS-9105).
 */
const DUPLICATE_GITHUB_APP_MESSAGE =
  'This GitHub App is already connected through another connection. Use that one, or register a separate App for this connection.'

/**
 * Whether a failed save was refused because the connection's endpoint is fixed. Exported for the
 * drawer, which refreshes its stale repository count when this arrives.
 */
export const getIsEndpointFixedRefusal = (error: unknown): boolean =>
  error instanceof ApiError && error.problemType === ENDPOINT_FIXED_PROBLEM_TYPE

/**
 * Whether a conflict is already reported beside the input the user has to fix, so an error toast
 * would only repeat it. Both drawers ask this before toasting a failed write.
 *
 * Derived from the report `getIntegrationSaveError` produced rather than from a second list of
 * problem types. A parallel list is one edit away from disagreeing with the dispatch beside it, and
 * the disagreement that silences a conflict — suppressed here, reported nowhere there — is exactly
 * the failure this dispatch exists to prevent.
 *
 * A conflict reported only above the form still toasts. `FormErrorBanner` renders at the top of the
 * field list, which a long drawer can have scrolled out of view, and a Save that appears to do
 * nothing is worse than one reported twice. Scoped to conflicts because every other failure — a
 * rejected credential, an unreachable host — has always shown both, and this is not the change to
 * revisit that in.
 */
export const getIsConflictReportedOnField = (error: unknown, saveError: IntegrationSaveError | undefined): boolean =>
  error instanceof ApiError && error.status === 409 && (saveError?.anchoredFieldKeys.length ?? 0) > 0

/**
 * The repository count the refusal carries, or `undefined` when the body is not the shape this
 * expects. Read from the typed field rather than from `detail`, which names the connection by its
 * id — an identifier the user has never seen and cannot act on.
 *
 * A non-positive count is treated as no count. The platform raises this refusal only when the count
 * is above zero, so a zero here is a body that disagrees with itself, and "cannot change while 0
 * repositories are onboarded" explains nothing.
 */
const getEndpointFixedCount = (error: ApiError): number | undefined => {
  const parsed = IntegrationEndpointFixedProblemSchema.safeParse(error.problemBody)
  if (!parsed.success || parsed.data.repositories <= 0) {
    return undefined
  }
  return parsed.data.repositories
}

/**
 * Anchors a refusal to the fields it names, or reports it above the form when this tool has none of
 * them. A refusal reported nowhere is the failure this dispatch exists to prevent, so an empty
 * anchor list must not silently swallow the message.
 */
const anchorOrReportAboveForm = (anchoredFieldKeys: string[], message: string): IntegrationSaveError =>
  anchoredFieldKeys.length > 0
    ? { anchoredFieldKeys, fieldMessage: message }
    : { anchoredFieldKeys: [], formMessage: message }

/**
 * What a `409` means, decided by the problem's `type`. Returns `undefined` for a conflict naming no
 * type this drawer knows, leaving the error toast to report the platform's own words — see
 * `IntegrationWriteOperation` for why only a create guesses.
 */
const getConflictSaveError = ({
  error,
  fields,
  operation,
}: {
  error: ApiError
  fields: IntegrationField[]
  operation: IntegrationWriteOperation
}): IntegrationSaveError | undefined => {
  if (error.problemType === ENDPOINT_FIXED_PROBLEM_TYPE) {
    return anchorOrReportAboveForm(
      fields.filter(field => field.isEndpointFixedOnceOnboarded).map(field => field.key),
      getEndpointFixedMessage(getEndpointFixedCount(error))
    )
  }

  if (error.problemType === DUPLICATE_GITHUB_APP_PROBLEM_TYPE) {
    const detail = error.problemDetail?.trim()
    return anchorOrReportAboveForm(
      fields.filter(field => field.key === APP_ID_FIELD_KEY).map(field => field.key),
      detail ? detail : DUPLICATE_GITHUB_APP_MESSAGE
    )
  }

  // The Name field is rendered by the drawers rather than by `TOOL_FIELD_CONFIG`, so every tool has
  // one and it is always there to anchor to.
  if (error.problemType === DUPLICATE_NAME_PROBLEM_TYPE) {
    return { anchoredFieldKeys: [NAME_FIELD_KEY], fieldMessage: DUPLICATE_NAME_MESSAGE }
  }

  if (error.problemType === CONCURRENT_MODIFICATION_PROBLEM_TYPE) {
    return { anchoredFieldKeys: [], formMessage: CONCURRENT_MODIFICATION_MESSAGE }
  }

  // Only a body carrying no `type` at all is guessed at, and only on a create. A typed conflict this
  // build does not know is a cause the client cannot name, so it falls through to the toast rather
  // than being reported as a name clash the user would rename and retry into.
  return operation === 'create' && error.problemType === undefined
    ? { anchoredFieldKeys: [NAME_FIELD_KEY], fieldMessage: DUPLICATE_NAME_MESSAGE }
    : undefined
}

/**
 * The drawer disables Save until a move carries fresh credentials, so reaching this means the client
 * and the platform disagreed about what moved — a URL the platform reduces to a different origin
 * than this page does, most likely. Written as an instruction rather than as a report of the
 * disagreement, since re-entering the credentials is what clears it either way.
 */
const ENDPOINT_MOVED_MESSAGE =
  'Pixee will not send a stored credential to an address you just entered. Re-enter every credential for this instance to move it, or put the original address back.'

const CREDENTIALS_UNREADABLE_MESSAGE =
  "Pixee could not read this instance's stored credentials, so it could not test the change and nothing was written. Re-enter every credential to replace them."

/**
 * The credentials the save-time probe actually sends. AppScan's webhook credentials and Sonar's
 * webhook secret are never the anchor for a validation reason — the probe authenticates with the API
 * credentials, and the webhook method is checked for shape rather than against the tool.
 */
const getProbedSecretKeys = (fields: IntegrationField[]): string[] =>
  fields.filter(field => field.isSecret && getSecretGroup(field) === 'primary').map(field => field.key)

/**
 * The field naming the host that failed. For a tool with URL overrides that is whichever override is
 * set, because it is the value that replaced the regional default; otherwise it is the tool's own
 * host field, or — for Checkmarx, which has none — the Region that resolves one.
 */
const getHostAnchorKeys = (fields: IntegrationField[], values: Record<string, string>): string[] => {
  const endpointFields = fields.filter(field => field.isEndpointDeciding)
  const filledOverride = endpointFields.find(field => field.isAdvanced && (values[field.key] ?? '').trim() !== '')
  if (filledOverride) {
    return [filledOverride.key]
  }
  const primaryHostField = endpointFields.find(field => !field.isAdvanced)
  return primaryHostField ? [primaryHostField.key] : []
}

type GetIntegrationSaveErrorArgs = {
  error: unknown
  fields: IntegrationField[]
  values: Record<string, string>
  /** The instance-card label for the tool, e.g. "SonarQube" — what the user sees on the card they opened. */
  toolLabel: string
  operation: IntegrationWriteOperation
}

/**
 * Turns a failed create or update into the field and form errors the drawer renders. Returns
 * `undefined` when the failure names nothing the drawer can anchor, in which case the error toast
 * `useShowMutationError` already raises is the whole of the report.
 */
export const getIntegrationSaveError = ({
  error,
  fields,
  values,
  toolLabel,
  operation,
}: GetIntegrationSaveErrorArgs): IntegrationSaveError | undefined => {
  if (!(error instanceof ApiError)) {
    return undefined
  }

  // A conflict is not a validation failure, so it carries no `reason` and is the one case decided
  // without consulting one — by the problem's `type`. See `IntegrationWriteOperation`.
  if (error.status === 409) {
    return getConflictSaveError({ error, fields, operation })
  }

  // Both of these anchor to the probed credentials, which is where the user has to act even though
  // a webhook secret may also be owed: those are the fields the message is about, and the message
  // itself asks for every credential rather than naming them one at a time.
  if (error.problemType === ENDPOINT_MOVED_PROBLEM_TYPE) {
    return { anchoredFieldKeys: getProbedSecretKeys(fields), fieldMessage: ENDPOINT_MOVED_MESSAGE }
  }

  if (error.problemType === CREDENTIALS_UNREADABLE_PROBLEM_TYPE) {
    return { anchoredFieldKeys: getProbedSecretKeys(fields), fieldMessage: CREDENTIALS_UNREADABLE_MESSAGE }
  }

  const parsedReason = IntegrationValidationFailureResponseSchema.safeParse(error.problemReason)
  if (!parsedReason.success) {
    return undefined
  }

  const detail = error.problemDetail?.trim()
  // The copy reads "…understands: {detail}. Your credentials…", which assumes the detail is a
  // fragment. Platform details are usually whole sentences, so the period is added only when the
  // detail does not already end one.
  const detailAsClause = detail === undefined ? undefined : detail.replace(/\.$/, '')
  // The platform's `detail` is the tool's own explanation of the refusal and holds no credential
  // material, so it leads; the sentence after it says what to do about it.
  const withDetail = (advice: string): string => (detail ? `${detail} ${advice}` : advice)

  return match(parsedReason.data)
    .with('authentication-rejected', () => ({
      anchoredFieldKeys: getProbedSecretKeys(fields),
      fieldMessage: withDetail('Check the credentials and try again.'),
    }))
    .with('insufficient-permissions', () => ({
      anchoredFieldKeys: getProbedSecretKeys(fields),
      fieldMessage: withDetail(
        `The credentials were accepted but lack the permission Pixee needs. Widen the grant in ${toolLabel}, then try again.`
      ),
    }))
    .with('host-unreachable', () => ({
      anchoredFieldKeys: getHostAnchorKeys(fields, values),
      fieldMessage: withDetail(
        "Pixee could not reach this address. Check the URL and that Pixee's network can reach it."
      ),
    }))
    .with('endpoint-not-allowed', () => ({
      anchoredFieldKeys: getHostAnchorKeys(fields, values),
      // The refused origin is named by `detail` and nowhere else in the response, so `detail` is the
      // message rather than a preamble to one. No Phase 2 tool returns this reason — only Datadog's
      // shipped allowlist does — but the branch exists because the contract still has five values.
      fieldMessage: detail ?? 'Pixee is not allowed to connect to this address.',
    }))
    .with('unexpected-response', () => ({
      anchoredFieldKeys: [],
      formMessage: `${toolLabel} answered, but not in a way Pixee understands${detailAsClause ? `: ${detailAsClause}` : ''}. Your credentials were not rejected — try again, or contact Support if it persists.`,
    }))
    .exhaustive()
}

/** The error text for one field, or `undefined` when the failure did not anchor to it. */
export const getFieldErrorText = (
  saveError: IntegrationSaveError | undefined,
  fieldKey: string
): string | undefined => {
  if (!saveError || !saveError.anchoredFieldKeys.includes(fieldKey)) {
    return undefined
  }
  return saveError.anchoredFieldKeys[0] === fieldKey ? saveError.fieldMessage : undefined
}

/** Whether a field is marked invalid by the failure. Every anchored field is, message or not. */
export const getIsFieldAnchored = (saveError: IntegrationSaveError | undefined, fieldKey: string): boolean =>
  saveError?.anchoredFieldKeys.includes(fieldKey) ?? false
